import React from 'react';
import { Link } from 'react-router-dom';
import { Button, LegalFooter, Logo, ThemeProvider, ThemeToggle, WhatsAppIcon } from '@/components/common';
import { WHATSAPP_SUPPORT_URL } from '@/lib/support';
import BrandPanel, { BrandPanelVariant } from './BrandPanel';

interface OtpAuthLayoutProps {
  /** Which story the desktop brand panel tells: signing in, or finding a plan. */
  panel?: BrandPanelVariant;
  children: React.ReactNode;
}

/**
 * Sign-in shell from the Figma "L" and "F" screens.
 *  - mobile  (<768):  full-bleed form, theme toggle in the header
 *  - tablet  (768+):  form in a centred card, Quick claim in the header (desktop too)
 *  - desktop (1024+): two equal halves — the form column and the dark brand panel
 */
const OtpAuthLayout: React.FC<OtpAuthLayoutProps> = ({ panel = 'sign-in', children }) => (
  <ThemeProvider className="tw-min-h-dvh tw-bg-ds-canvas tw-text-ds-text-primary">
    <div className="tw-flex tw-min-h-dvh">
      <div className="tw-flex tw-w-full tw-flex-col tw-px-6 tw-pb-7 tw-pt-4 md:tw-px-10 md:tw-pb-8 md:tw-pt-10 lg:tw-w-1/2 lg:tw-shrink-0 lg:tw-px-20 lg:tw-py-9">
        <header className="tw-flex tw-w-full tw-items-center tw-justify-between">
          <Link
            to="/"
            aria-label="Protega home"
            className="tw-rounded focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand"
          >
            <Logo />
          </Link>
          <div className="tw-flex tw-items-center tw-gap-2.5">
            {/* Tablet and desktop; the mobile screens don't have it (per Figma). */}
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={WhatsAppIcon}
              href={WHATSAPP_SUPPORT_URL}
              target="_blank"
              className="tw-hidden md:tw-inline-flex"
            >
              Quick claim
            </Button>
            <ThemeToggle />
          </div>
        </header>

        <div className="tw-h-7 tw-shrink-0 md:tw-h-auto md:tw-min-h-10 md:tw-flex-1" />

        <main className="tw-w-full md:tw-mx-auto md:tw-w-[520px] md:tw-rounded-3xl md:tw-border md:tw-border-ds-border-default md:tw-bg-ds-surface md:tw-p-10 lg:tw-w-full lg:tw-max-w-[454px] lg:tw-rounded-none lg:tw-border-0 lg:tw-bg-transparent lg:tw-p-0">
          {children}
        </main>

        {/* Pushes the footer to the bottom of the screen at every size. */}
        <div className="tw-min-h-6 tw-flex-1 md:tw-min-h-10" />

        <LegalFooter layout="centered" className="lg:tw-hidden" />
        <LegalFooter layout="split" className="tw-hidden lg:tw-flex" />
      </div>

      <div className="tw-hidden lg:tw-flex lg:tw-w-1/2 lg:tw-shrink-0">
        <BrandPanel variant={panel} />
      </div>
    </div>
  </ThemeProvider>
);

export default OtpAuthLayout;
